import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowRightIcon, CircleCheckIcon, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { cn } from '@/lib/utils.ts'
import { Button } from '@/components/ui/button.tsx'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx'
import { documentStatusLabels } from '@/features/documents/constants.ts'
import { useChangeDocumentStatus } from '@/features/documents/hooks/use-change-document-status.ts'
import { getDocumentQueryOptions } from '@/features/documents/hooks/use-get-document.ts'
import { Document } from '@/features/documents/types.ts'

interface ChangeStatusDialogProps {
  document?: Document
  isOpen?: boolean
  onClose?: () => void
}

// Every status the document can hold, not just the two this dialog toggles
// between: the toolbar button is always available, so a document sitting in
// `idle` or `processing` reaches this preview too. Typing it against
// Document['status'] means a new enum value breaks the build here rather than
// silently falling back to the wrong badge.
const statusStyles: Record<Document['status'], string> = {
  idle: 'bg-gray-100 text-gray-800 border-gray-200',
  processing: 'bg-blue-100 text-blue-800 border-blue-200',
  in_review: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  ready: 'bg-green-100 text-green-800 border-green-200',
}

export const ChangeStatusDialog: React.FC<ChangeStatusDialogProps> = ({
  document,
  isOpen,
  onClose,
}) => {
  const queryClient = useQueryClient()
  const changeDocumentStatus = useChangeDocumentStatus({
    async onSuccess() {
      toast('Stato cambiato correttamente ', { icon: <CircleCheckIcon /> })

      await queryClient.invalidateQueries({
        queryKey: getDocumentQueryOptions({ documentId: document!.id })
          .queryKey,
      })

      onClose?.()
    },
  })

  const handleOpenChange = (isOpen: boolean) => (isOpen ? onClose?.() : null)

  const handleConfirmClick = async () => {
    await changeDocumentStatus.mutateAsync({
      documentId: document!.id,
      status: document?.status === 'in_review' ? 'ready' : 'in_review',
    })
  }

  const currentStatus = document?.status
  const newStatus: Document['status'] =
    currentStatus === 'in_review' ? 'ready' : 'in_review'

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="mb-2">Cambia stato</DialogTitle>
          <DialogDescription>
            Confermi di voler cambiare lo stato del documento?
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center gap-4 py-4">
          {currentStatus && (
            <>
              <div
                className={cn(
                  'rounded-full border px-4 py-2 text-sm font-medium',
                  statusStyles[currentStatus]
                )}
              >
                {documentStatusLabels[currentStatus]}
              </div>
              <ArrowRightIcon className="size-5 text-gray-400" />
            </>
          )}
          <div
            className={cn(
              'rounded-full border px-4 py-2 text-sm font-medium',
              statusStyles[newStatus]
            )}
          >
            {documentStatusLabels[newStatus]}
          </div>
        </div>

        <DialogFooter className="mt-2 flex items-end gap-x-1">
          <Button variant="outline" onClick={() => onClose?.()}>
            Annulla
          </Button>
          <Button
            type="submit"
            onClick={handleConfirmClick}
            disabled={changeDocumentStatus.isPending}
          >
            {changeDocumentStatus.isPending && (
              <Loader2 className="mr-2 size-4 animate-spin" />
            )}
            Conferma
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
